import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Send } from "lucide-react";

export default function Messages() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    loadThreads();
  }, [user, authLoading, nav]);

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
    const toParam = params.get("to");
    if (toParam) {
      // find existing thread with that user, or create a placeholder
      let existing = data.find(t => t.other_id === toParam);
      if (!existing) {
        try {
          const { data: u } = await api.get(`/users/${toParam}`);
          existing = { other_id: u.id, other_name: u.name, last_message: "", last_at: "", unread: 0 };
        } catch (err) {
          console.error("[messages] load user failed", err);
        }
      }
      if (existing) setSelected(existing);
    } else if (data.length && !selected) {
      setSelected(data[0]);
    }
  };

  useEffect(() => {
    if (!selected) return;
    api.get(`/messages/with/${selected.other_id}`).then(r => setMsgs(r.data));
  }, [selected]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selected) return;
    await api.post("/messages", { recipient_id: selected.other_id, content: text });
    setText("");
    const { data } = await api.get(`/messages/with/${selected.other_id}`);
    setMsgs(data);
    loadThreads();
  };

  if (authLoading || !user) return null;

  return (
    <div data-testid="messages-page" className="pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="overline text-[#143122]">{t("inbox")}</div>
        <h1 className="mt-2 font-serif-display text-4xl text-[#143122]">{t("nav_messages")}</h1>

        <div className="mt-8 grid md:grid-cols-12 gap-6 border border-[#E6E2D8] rounded-lg overflow-hidden bg-white min-h-[520px]">
          <aside className="md:col-span-4 border-r border-[#E6E2D8] bg-[#F4F1E9] max-h-[70vh] overflow-y-auto">
            {threads.length === 0 ? (
              <div className="p-6 text-sm text-[#1A1F1C]/60">{t("thread_empty")}</div>
            ) : threads.map((th) => (
              <div
                key={th.other_id}
                data-testid={`thread-${th.other_id}`}
                className={`w-full p-4 border-b border-[#E6E2D8] transition-colors ${selected?.other_id === th.other_id ? "bg-white" : "hover:bg-white/50"}`}
              >
                <div className="flex items-center justify-between">
                  <Link
                    to={`/users/${th.other_id}`}
                    data-testid={`thread-name-link-${th.other_id}`}
                    className="font-serif-display text-lg text-[#143122] hover:underline"
                  >
                    {th.other_name}
                  </Link>
                  {th.unread > 0 && <span className="text-xs bg-[#143122] text-white rounded-full px-2 py-0.5">{th.unread}</span>}
                </div>
                <button
                  type="button"
                  data-testid={`thread-open-${th.other_id}`}
                  onClick={() => setSelected(th)}
                  className="w-full text-left text-xs text-[#1A1F1C]/60 mt-1 line-clamp-1 hover:text-[#143122]"
                >
                  {th.last_message || t("open_conversation")}
                </button>
              </div>
            ))}
          </aside>

          <section className="md:col-span-8 flex flex-col max-h-[70vh]">
            {selected ? (
              <>
                <div className="p-4 border-b border-[#E6E2D8] bg-white flex items-center justify-between gap-2">
                  <Link
                    to={`/users/${selected.other_id}`}
                    data-testid={`message-header-profile-link-${selected.other_id}`}
                    className="font-serif-display text-xl text-[#143122] hover:underline"
                  >
                    {selected.other_name}
                  </Link>
                  <Link
                    to={`/users/${selected.other_id}`}
                    className="text-xs text-[#143122] hover:underline"
                  >
                    {t("view_profile")}
                  </Link>
                </div>
                <div ref={listRef} data-testid="message-list" className="flex-1 overflow-y-auto p-4 space-y-3">
                  {msgs.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-lg px-4 py-2 text-sm ${mine ? "bg-[#143122] text-white" : "bg-[#F4F1E9] text-[#1A1F1C]"}`}>
                          {m.content}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} className="p-4 border-t border-[#E6E2D8] flex gap-2 bg-white">
                  <input
                    data-testid="chat-input"
                    className="kav-input flex-1"
                    placeholder={t("write_message")}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                  <button data-testid="chat-send-btn" type="submit" className="kav-btn-primary">
                    <Send size={16} strokeWidth={1.75} />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-[#1A1F1C]/60">
                {t("thread_empty")}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
